<template>
  <div>
    <Head title="Reports" />
    <h1 class="mb-8 text-3xl font-bold">Reports</h1>
    <form class="flex flex-wrap -mb-8 -mr-6 pb-3" @submit.prevent="store">
      <text-input
        v-model="form.from" :error="form.errors.from" class="pb-4 pr-6 w-full lg:w-1/4"
        :label="$t('global.from')" type="date"
      />
      <text-input
        v-model="form.till" :error="form.errors.till" class="pb-4 pr-6 w-full lg:w-1/4"
        :label="$t('global.till')" type="date"
      />
      <select-multiple
        v-model="selectUsers"
        :default-options="users"
        :error="form.errors.user_id"
        :label="$t('interventions.user')"
        class="pr-6 pb-4 w-full lg:w-2/4"
      />
      <select-multiple
        v-model="selectTreatments"
        :error="form.errors.treatment_type_id"
        class="pr-6 pb-4 w-full lg:w-2/4"
        :default-options="treatment_types"
        :label="$t('treatments.treatment')"
      />
      <select-multiple
        v-model="selectInterventions"
        :default-options="intervention_types"
        :error="form.errors.intervention_type_id"
        :label="$t('interventions.intervention_type')"
        class="pr-6 pb-4 w-full lg:w-2/4"
      />
      <select-multiple
        v-model="selectLocations"
        :default-options="locations"
        :error="form.errors.location_id"
        :label="$t('interventions.location')"
        class="pr-6 pb-4 w-full lg:w-2/4"
      />
      <select-dropdown
        v-model="form.gender"
        :options="genders"
        :error="form.errors.gender"
        :label="$t('applicants.gender')"
        class="pr-6 pb-4 w-full lg:w-2/4"
      />
      <select-dropdown
        v-model="form.pane_type"
        :error="form.errors.pane_type"
        class="pr-6 pb-4 w-full lg:w-2/4"
        :options="pane_types"
        :label="$t('interventions.send_type')"
      />
      <div class="pr-6 pb-8 w-full lg:w-4/4 flex">
        <loading-button :loading="form.processing" class="btn-indigo mr-2" type="submit">
          {{ $t('global.search') }}
        </loading-button>
        <a href="#" class="btn-green" @click="exportData()">{{ $t('global.export') }}</a>
      </div>
    </form>
    <cards
      :report="report"
      @exportDataDetails="(detailValue) => exportData(detailValue, true, true)"
    />
    <div class="grid grid-cols-1 2xl:grid-cols-2 xl:gap-4 my-4">
      <major-table
        :items="report.major.by_age_range"
        :total="report.major.applicants"
        title="By Age Range"
        subtitle="Age"
        @exportDataDetails="(detailValue) => exportData('age_range', 'name', detailValue)"
      />
      <major-table
        :items="report.major.by_zip_code"
        :total="report.major.applicants"
        title="By Zip Code"
        subtitle="Zip Code"
        @exportDataDetails="(detailValue) => exportData('zip_code', 'name', detailValue)"
      />
      <major-table
        :items="report.major.by_gender"
        :total="report.major.applicants"
        title="By Gender"
        subtitle="Gender"
        @exportDataDetails="(detailValue) => exportData('gender_id', 'id', detailValue)"
      />
      <applicants-table
        :items="report.applicants"
        :total="report.major.applicants"
        title="By User"
        subtitle="User"
        @exportDataDetails="function(detailValue){
          if(filters.treatment_type_id){
            exportData('treatment_type_id', 'treatment_type_id', detailValue)
          }else{
            exportData('user_id', 'user_id', detailValue)
          }
        }"
      />
      <major-table
        :items="report.major.by_seen"
        :total="report.major.interventions"
        title="By Seen"
        subtitle="Seen"
        @exportDataDetails="(detailValue) => exportData('seen', 'id', detailValue)"
      />
      <major-table
        :items="report.major.by_type"
        :total="report.major.interventions"
        title="By Type"
        subtitle="Type"
        @exportDataDetails="(detailValue) => exportData('intervention_type_id', 'id', detailValue)"
      />
    </div>
  </div>
</template>

<script>
import {Head} from '@inertiajs/inertia-vue3'
import Layout from '@/Shared/Layout'
import Cards from './Panels/Cards'
import MajorTable from './Panels/MajorTable'
import ApplicantsTable from './Panels/ApplicantsTable'
import TextInput from '@/Shared/TextInput'
import SelectMultiple from '@/Shared/SelectMultiple'
import LoadingButton from '@/Shared/LoadingButton'
import SelectDropdown from '@/Shared/SelectDropdown'
import map from 'lodash/map'
import filter from 'lodash/filter'
import pick from 'lodash/pick'
import RadioHorizontal from '@/Shared/RadioHorizontal'

export default {
  components: {
    SelectMultiple,
    LoadingButton,
    ApplicantsTable,
    TextInput,
    Head,
    Cards,
    MajorTable,
    SelectDropdown,
    RadioHorizontal,
  },
  layout: Layout,
  props: {
    report: Object,
    users: Object,
    intervention_types: Object,
    treatment_types: Object,
    filters: Object,
    locations: Object,
    genders: Object,
    pane_types: Object,
  },
  data() {
    return {
      form: this.$inertia.form({
        from: this.filters.from,
        till: this.filters.till,
        user_id: [],
        intervention_type_id: [],
        treatment_type_id: [],
        location_id: this.filters.location_id ?? null,
        gender: this.filters.gender ?? null,
        pane_type: this.filters.pane_type ?? null,
      }),
      selectUsers: [],
      selectInterventions: [],
      selectTreatments: [],
      selectLocations: [],
    }
  },
  computed: {
    parameterUrl() {
      const myObject = this.filters

      // Step 1: Extract keys and values from the object
      const keys = Object.keys(myObject)
      const values = Object.values(myObject)

      // Step 2: Combine keys and values with equal signs
      const combined = keys.map((key, index) => `${key}=${values[index]}`)

      // Step 3: Join key-value pairs with ampersands
      const result = combined.join('&')

      return result
    },
  },
  mounted() {
    if (this.filters.user_id) {
      this.selectUsers = this.filterAndMap(this.filters.user_id, this.users)
    }
    if (this.filters.intervention_type_id) {
      this.selectInterventions = this.filterAndMap(this.filters.intervention_type_id, this.intervention_types)
    }
    if (this.filters.treatment_type_id) {
      this.selectTreatments = this.filterAndMap(this.filters.treatment_type_id, this.treatment_types)
    }
    if (this.filters.location_id) {
      this.selectLocations = this.filterAndMap(this.filters.location_id, this.locations)
    }
  },
  methods: {
    store() {
      this.form.user_id = map(this.selectUsers, 'id')
      this.form.intervention_type_id = map(this.selectInterventions, 'id')
      this.form.treatment_type_id = map(this.selectTreatments, 'id')
      this.form.location_id = map(this.selectLocations, 'id')
      this.form.get('/reports')
    },
    exportData(detailItem, detailAttribute, detailValue) {
      let url = 'export'
      let parameterDetail = ''
      if (detailValue) {
        url = 'export-details'
        parameterDetail = '&detailItem=' + detailItem + '&detailValue=' + detailValue[detailAttribute]
      }
      window.location.href = '/reports/' + url + '?' + this.parameterUrl + parameterDetail
    },
    filterAndMap(filterIds, sourceArray) {
      const idsAsIntegers = filterIds.map(id => parseInt(id, 10))
      return sourceArray.filter(obj => idsAsIntegers.includes(obj.id)).map(obj => ({id: obj.id, name: obj.name}))
    },
  },
}
</script>
